import { Page } from "@/pages/explorer/types";
import { StreamlitNotebookJobListResponse } from "@/pages/query/types";
import { useAppState } from "@/modules/app/useAppContext";
import { formatBytes, formatCost, formatMilliseconds } from "@/helpers/utils";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { Link } from "react-router-dom";
import { FilterType, TableHeaderType } from "@/components/tables/types";
import { useMemo } from "react";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import QueryTextWithHover from "@/pages/query/QueryText";
import { FilterTagWrapper } from "@/components/tags";
import { FilterApplicableFor } from "@/components/tags/types";

const SORT_SEARCH_KEY = "sortAttribute";
const SORT_ORDER_SEARCH_KEY = "sortOrder";

export const CommonJobListTable = ({
  basePath,
  tableData,
  page,
  pageSize,
  totalPages,
  totalItems,
  setPage,
  updatePageSize,
  handleFilterChange,
  filters = {},
  isLoading = false,
  showQueryTextOnHover = false,
  showDatabaseColumn = false,
  applicableFor,
}: {
  basePath: string;
  tableData: Page<StreamlitNotebookJobListResponse> | undefined;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  updatePageSize: (size: number) => void;
  handleFilterChange?: (key: string, value: string | string[]) => void;
  totalPages: number;
  totalItems: number;
  filters?: Record<string, string[]>;
  isLoading?: boolean;
  showQueryTextOnHover?: boolean;
  showDatabaseColumn?: boolean;
  applicableFor?: FilterApplicableFor;
}) => {
  const { currency } = useAppState();
  const { searchParams, updateSearchParamsByKey, deleteSearchParamsByKeyValue } =
    useFiltersBySearchParams();
  const warehouseFilter = useMemo(() => {
    return searchParams.getAll("warehouses") || [];
  }, [searchParams]);
  const databaseFilter = useMemo(() => {
    return searchParams.getAll("databases") || [];
  }, [searchParams]);

  // Create filter tags for FilterTagWrapper
  const filterTags = useMemo(() => {
    const tags: Record<
      string,
      { label: string; filterStr: string[]; onclose: (removedFilter?: string) => void }
    > = {
      warehouseFilter: {
        label: "Warehouses",
        filterStr: warehouseFilter,
        onclose: (removedFilter?: string) => {
          deleteSearchParamsByKeyValue({ warehouses: removedFilter });
        },
      },
    };
    if (showDatabaseColumn) {
      tags.databaseFilter = {
        label: "Databases",
        filterStr: databaseFilter,
        onclose: (removedFilter?: string) => {
          deleteSearchParamsByKeyValue({ databases: removedFilter });
        },
      };
    }
    return tags;
  }, [warehouseFilter, databaseFilter, deleteSearchParamsByKeyValue, showDatabaseColumn]);

  const tableHeaders: TableHeaderType<StreamlitNotebookJobListResponse>[] = useMemo(() => {
    const headers: TableHeaderType<StreamlitNotebookJobListResponse>[] = [
      {
        id: 1,
        label: "Workload ID",
        uid: "workflow_id",
        width: "25%",
        render: (item: StreamlitNotebookJobListResponse) => (
          <Link
            to={`/query/${basePath}/${encodeURIComponent(item.hash)}?query_summary=${encodeURIComponent(item.query_summary)}`}
            className="al-text-brand condense-text cursor-pointer"
          >
            {showQueryTextOnHover ? (
              <QueryTextWithHover queryText={item.query_summary} queryHash={item.hash} />
            ) : (
              item.query_summary
            )}
          </Link>
        ),
      },
      {
        id: 2,
        label: "Total Cost",
        uid: "query_cost",
        render: (item: StreamlitNotebookJobListResponse) => (
          <div>{formatCost(item.cost, currency)}</div>
        ),
        sort: {
          sortSearchKey: SORT_SEARCH_KEY,
          sortOrderSearchKey: SORT_ORDER_SEARCH_KEY,
          sortParamValue: "cost",
        },
      },
      {
        id: 3,
        label: "Bytes Scanned",
        uid: "bytes_scanned",
        render: (item: StreamlitNotebookJobListResponse) => (
          <div>{formatBytes(item.bytes_scanned)}</div>
        ),
        sort: {
          sortSearchKey: SORT_SEARCH_KEY,
          sortOrderSearchKey: SORT_ORDER_SEARCH_KEY,
        },
      },
      {
        id: 4,
        label: "Execution Time",
        uid: "execution_time",
        render: (item: StreamlitNotebookJobListResponse) => (
          <div>{formatMilliseconds(item.execution_time)}</div>
        ),
        sort: {
          sortSearchKey: SORT_SEARCH_KEY,
          sortOrderSearchKey: SORT_ORDER_SEARCH_KEY,
        },
      },
      {
        id: 5,
        label: "Runs",
        uid: "runs",
        width: "5%",
        render: (item: StreamlitNotebookJobListResponse) => <div>{item.job_count}</div>,
        sort: {
          sortSearchKey: SORT_SEARCH_KEY,
          sortOrderSearchKey: SORT_ORDER_SEARCH_KEY,
          sortParamValue: "job_count",
        },
      },
    ];

    if (showDatabaseColumn) {
      headers.push({
        id: 6,
        label: "Database",
        uid: "databases",
        width: "15%",
        render: (item: StreamlitNotebookJobListResponse) => (
          <div className="al-break-all">
            {item.database_names.length > 0 ? item.database_names.join(", ") : "—"}
          </div>
        ),
        filter: {
          id: "databases",
          label: "Databases",
          searchKey: "databases",
          filterType: FilterType.DROPDOWN,
          value: databaseFilter,
          options: filters?.databases?.map(db => ({ label: db, value: db })) || [],
          onChange: (value: string[]) => {
            handleFilterChange?.("databases", value);
            updateSearchParamsByKey({ databases: value });
          },
        },
      });
    }

    headers.push({
      id: 7,
      label: "Warehouses",
      uid: "warehouses",
      width: "20%",
      render: (item: StreamlitNotebookJobListResponse) => (
        <div className="al-break-all">{item.warehouse_names.join(", ")}</div>
      ),
      filter: {
        id: "warehouses",
        label: "Warehouses",
        searchKey: "warehouses",
        filterType: FilterType.DROPDOWN,
        value: warehouseFilter,
        options: filters?.warehouses?.map(wh => ({ label: wh, value: wh })) || [],
        onChange: (value: string[]) => {
          handleFilterChange?.("warehouses", value);
          updateSearchParamsByKey({ warehouses: value });
        },
      },
    });

    return headers;
  }, [
    basePath,
    currency,
    filters?.warehouses,
    filters?.databases,
    handleFilterChange,
    showQueryTextOnHover,
    showDatabaseColumn,
    updateSearchParamsByKey,
    warehouseFilter,
    databaseFilter,
  ]);
  return (
    <Stack direction={"column"} gap={"lg"}>
      <FilterTagWrapper filters={filterTags} applicableFor={applicableFor} />
      <DataTable
        isLoading={isLoading}
        header={tableHeaders}
        data={tableData ?? { items: [] }}
        idKey={(item: StreamlitNotebookJobListResponse) => item.hash}
        defaultSort={{ sortParamValue: "cost", order: "desc" }}
      />
      <Paginate
        isLoading={isLoading}
        itemCount={totalItems || 0}
        numPages={totalPages || 0}
        page={page}
        pageSize={pageSize}
        onPageClick={setPage}
        onPageSizeChange={updatePageSize}
        showPageSize
      />
    </Stack>
  );
};
